/**
 * Reusable diagnostic visual blocks — shared between the personal
 * report (`/app/diagnostic/report`) and the dossier-scoped IMES section.
 */

export function DimensionBar({ label, value = 0, weight, tone }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-[#374151] font-medium">{label}</span>
        <span className="text-[#8B8B8F] font-mono tabular-nums">{Number(value).toFixed(1)} / 100 · {weight}</span>
      </div>
      <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${tone} transition-all`} style={{ width: `${Math.max(2, value)}%` }} />
      </div>
    </div>
  );
}

export function AxisBar({ label, value = 0 }) {
  const tone = value >= 70 ? "bg-emerald-500" : value >= 50 ? "bg-[#0B2545]" : value >= 30 ? "bg-amber-500" : "bg-red-500";
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-[#1F2937] font-medium">{label}</span>
        <span className="text-[#5B5B62] font-mono tabular-nums">{Number(value).toFixed(0)}</span>
      </div>
      <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${Math.max(3, value)}%` }} />
      </div>
    </div>
  );
}

export function IndexCard({ label, value = 0, isRisk }) {
  const good = isRisk ? value < 40 : value >= 60;
  const bad  = isRisk ? value >= 70 : value < 40;
  const tone = good ? "border-emerald-300 bg-emerald-50 text-emerald-900"
             : bad  ? "border-red-300 bg-red-50 text-red-900"
             : "border-[#E5E7EB] bg-[#F8F7F3] text-[#0B2545]";
  return (
    <div className={`border rounded-md p-3 ${tone}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-display text-2xl mt-1 tabular-nums">{Number(value).toFixed(0)}</div>
    </div>
  );
}

export function ScenarioCard({ scenario }) {
  const p10 = scenario.projections?.["10"] || {};
  const p5  = scenario.projections?.["5"] || {};
  const toneMap = {
    pessimiste: "border-red-300 bg-red-50/40",
    realiste:   "border-[#0B2545]/25 bg-[#EEF2F7]/40",
    optimiste:  "border-emerald-300 bg-emerald-50/40",
    ambitieux:  "border-[#C5A880] bg-[#FBF6EA]/60",
  };
  return (
    <div className={`border-2 rounded-lg p-5 ${toneMap[scenario.key] || "border-[#E5E7EB] bg-white"}`} data-testid={`scenario-${scenario.key}`}>
      <div className="font-display text-lg text-[#0B2545] mb-1">{scenario.label_fr}</div>
      <div className="text-xs text-[#5B5B62] leading-snug mb-4">{scenario.narrative_fr}</div>
      <div className="space-y-2 text-xs">
        <div><span className="text-[#8B8B8F]">Revenu 5 ans :</span> <span className="font-mono text-[#1F2937] font-bold tabular-nums"> {formatCad(p5.projected_income_cad)}</span></div>
        <div><span className="text-[#8B8B8F]">Revenu 10 ans :</span> <span className="font-mono text-[#1F2937] font-bold tabular-nums"> {formatCad(p10.projected_income_cad)}</span></div>
        <div><span className="text-[#8B8B8F]">Valeur nette 10 ans :</span> <span className="font-mono text-[#1F2937] font-bold tabular-nums"> {formatCad(p10.projected_net_worth_cad)}</span></div>
        <div className="pt-2 mt-2 border-t border-current/10 flex justify-between text-[10px] font-mono uppercase tracking-widest">
          <span>Autonomie {scenario.autonomy_index}</span>
          <span>Risque {scenario.risk_index}</span>
        </div>
      </div>
    </div>
  );
}

export function AnalysisBlock({ icon: Icon, title, items = [], tone, wide }) {
  if (!items || items.length === 0) return null;
  const toneMap = {
    emerald: "border-emerald-300 bg-white text-emerald-900",
    amber:   "border-amber-300 bg-white text-amber-900",
    blue:    "border-blue-300 bg-white text-blue-900",
    red:     "border-red-300 bg-white text-red-900",
    navy:    "border-[#0B2545]/40 bg-white text-[#0B2545]",
    gold:    "border-[#C5A880] bg-white text-[#7A5B14]",
    grey:    "border-[#E5E7EB] bg-white text-[#5B5B62]",
  };
  return (
    <div className={`border-2 rounded-lg p-5 ${toneMap[tone] || toneMap.navy} ${wide ? "md:col-span-2" : ""}`}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="h-4 w-4" strokeWidth={2} />}
        <h3 className="font-display text-base">{title}</h3>
        <span className="ml-auto text-[10px] font-mono opacity-60">{items.length}</span>
      </div>
      <ul className="space-y-2 text-sm leading-relaxed">
        {items.map((item, i) => <li key={i} className="flex gap-2"><span className="opacity-40">•</span><span>{item}</span></li>)}
      </ul>
    </div>
  );
}

export function formatCad(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(v);
}

export const AXIS_LABELS = {
  patrimoine: "Patrimoine",
  emploi: "Emploi",
  employabilite: "Employabilité",
  education: "Éducation",
  entrepreneuriat: "Entrepreneuriat",
  reseau: "Réseau",
  resilience: "Résilience",
  autonomie: "Autonomie",
};

export const INDEX_LABELS = {
  idx_mobility: "Mobilité",
  idx_resilience: "Résilience",
  idx_stability: "Stabilité",
  idx_autonomie: "Autonomie",
  idx_potential: "Potentiel",
  idx_risk: "Risque",
};
